// CANONICAL COPY (GH #7115). src/constants/frameworks/euAiAct.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

// EU AI Act — Regulation (EU) 2024/1689 of 13 June 2024 — framework mapping
// to TCF controls. Only operative articles that impose implementable duties
// on providers, deployers, importers, distributors or GPAI providers are
// included. Articles covering authority competence, delegated acts and
// transitional provisions are out of scope.
//
// Reuses SCF-AIG-001 … SCF-AIG-012 introduced with ISO/IEC 42001 (#724) and
// adds SCF-AIG-013 … SCF-AIG-017 for AI-Act-specific duties (prohibited
// practices, FRIA, conformity assessment, post-market monitoring, GPAI).
//
// Multiple mappings with the same externalId are intentional — the last entry
// per externalId is used for the scfLookup (cross-reference display), while
// all entries contribute to controlToFrameworks and externalRefs.

const FRAMEWORK_EU_AI_ACT = {
  id: 'fw-eu-ai-act',
  type: 'law',
  name: 'EU AI Act',
  shortName: 'EU AI Act',
  abbrev: 'AIACT',
  description:
    'Regulation (EU) 2024/1689 — harmonised rules on artificial intelligence. Binding law covering prohibited practices, high-risk AI systems, transparency, general-purpose AI models, post-market monitoring and penalties.',
  color: '#0ea5e9',
  version: '2024/1689',
  jurisdiction: 'EU',
  sectionKeyDepth: 1,
  // One section per mapped article, named with the article's own title from
  // Regulation (EU) 2024/1689. The mappings below are banded by chapter; those
  // bands are kept as separate sections here so each heading names the article
  // it actually covers rather than repeating a chapter name a dozen times.
  sections: [
    { key: 'ART4', name: 'Art. 4 — AI literacy' },
    { key: 'ART5', name: 'Art. 5 — Prohibited AI practices' },
    { key: 'ART9', name: 'Art. 9 — Risk management system' },
    { key: 'ART10', name: 'Art. 10 — Data and data governance' },
    { key: 'ART11', name: 'Art. 11 — Technical documentation' },
    { key: 'ART12', name: 'Art. 12 — Record-keeping' },
    { key: 'ART13', name: 'Art. 13 — Transparency and provision of information to deployers' },
    { key: 'ART14', name: 'Art. 14 — Human oversight' },
    { key: 'ART15', name: 'Art. 15 — Accuracy, robustness and cybersecurity' },
    { key: 'ART16', name: 'Art. 16 — Obligations of providers of high-risk AI systems' },
    { key: 'ART17', name: 'Art. 17 — Quality management system' },
    { key: 'ART18', name: 'Art. 18 — Documentation keeping' },
    { key: 'ART19', name: 'Art. 19 — Automatically generated logs' },
    { key: 'ART20', name: 'Art. 20 — Corrective actions and duty of information' },
    { key: 'ART21', name: 'Art. 21 — Cooperation with competent authorities' },
    { key: 'ART22', name: 'Art. 22 — Authorised representatives of providers' },
    { key: 'ART23', name: 'Art. 23 — Obligations of importers' },
    { key: 'ART24', name: 'Art. 24 — Obligations of distributors' },
    {
      key: 'ART25',
      name: 'Art. 25 — Responsibilities along the AI value chain',
    },
    { key: 'ART26', name: 'Art. 26 — Obligations of deployers of high-risk AI systems' },
    { key: 'ART27', name: 'Art. 27 — Fundamental rights impact assessment' },
    { key: 'ART40', name: 'Art. 40 — Harmonised standards and standardisation deliverables' },
    { key: 'ART41', name: 'Art. 41 — Common specifications' },
    { key: 'ART43', name: 'Art. 43 — Conformity assessment' },
    { key: 'ART47', name: 'Art. 47 — EU declaration of conformity' },
    { key: 'ART48', name: 'Art. 48 — CE marking' },
    { key: 'ART49', name: 'Art. 49 — Registration' },
    { key: 'ART50', name: 'Art. 50 — Transparency obligations for providers and deployers' },
    { key: 'ART51', name: 'Art. 51 — Classification of general-purpose AI models' },
    { key: 'ART52', name: 'Art. 52 — Procedure for general-purpose AI models with systemic risk' },
    { key: 'ART53', name: 'Art. 53 — Obligations for providers of general-purpose AI models' },
    { key: 'ART54', name: 'Art. 54 — Authorised representatives of GPAI model providers' },
    {
      key: 'ART55',
      name: 'Art. 55 — Obligations for providers of GPAI models with systemic risk',
    },
    { key: 'ART56', name: 'Art. 56 — Codes of practice' },
    { key: 'ART71', name: 'Art. 71 — EU database for high-risk AI systems' },
    { key: 'ART72', name: 'Art. 72 — Post-market monitoring by providers' },
    { key: 'ART73', name: 'Art. 73 — Reporting of serious incidents' },
    { key: 'ART79', name: 'Art. 79 — Procedure for AI systems presenting a risk' },
    { key: 'ART95', name: 'Art. 95 — Codes of conduct for voluntary application' },
    { key: 'ART99', name: 'Art. 99 — Penalties' },
  ],
  mappings: [
    // ── Ch. I — General provisions ───────────────────────────────────────
    { externalId: 'AIACT-ART4', scfId: 'SCF-AIG-002', ref: 'Art. 4' },
    { primary: true, externalId: 'AIACT-ART4', scfId: 'SCF-PPL-003', ref: 'Art. 4' }, // primary — AI literacy training

    // ── Ch. II — Prohibited AI practices ────────────────────────────────
    { externalId: 'AIACT-ART5', scfId: 'SCF-AIG-013', ref: 'Art. 5' }, // primary

    // ── Ch. III §2 — Requirements for high-risk AI systems ──────────────
    { externalId: 'AIACT-ART9', scfId: 'SCF-RSK-001', ref: 'Art. 9' }, // risk assessment process (secondary)
    { primary: true, externalId: 'AIACT-ART9', scfId: 'SCF-AIG-018', ref: 'Art. 9' }, // primary — AI risk management system (whole-lifecycle)
    { externalId: 'AIACT-ART10', scfId: 'SCF-AIG-009', ref: 'Art. 10' }, // primary
    { externalId: 'AIACT-ART11', scfId: 'SCF-AIG-008', ref: 'Art. 11' }, // primary
    { externalId: 'AIACT-ART12', scfId: 'SCF-OPS-006', ref: 'Art. 12' },
    { primary: true, externalId: 'AIACT-ART12', scfId: 'SCF-AIG-007', ref: 'Art. 12' }, // primary
    { externalId: 'AIACT-ART13', scfId: 'SCF-AIG-008', ref: 'Art. 13' }, // primary
    { externalId: 'AIACT-ART14', scfId: 'SCF-AIG-011', ref: 'Art. 14' }, // primary
    { externalId: 'AIACT-ART15', scfId: 'SCF-AIG-006', ref: 'Art. 15' }, // primary

    // ── Ch. III §3 — Obligations of operators ───────────────────────────
    { externalId: 'AIACT-ART16', scfId: 'SCF-AIG-002', ref: 'Art. 16' }, // primary
    { externalId: 'AIACT-ART17', scfId: 'SCF-MSS-004', ref: 'Art. 17' },
    { primary: true, externalId: 'AIACT-ART17', scfId: 'SCF-AIG-001', ref: 'Art. 17' }, // primary
    { externalId: 'AIACT-ART18', scfId: 'SCF-GOV-011', ref: 'Art. 18' },
    { primary: true, externalId: 'AIACT-ART18', scfId: 'SCF-AIG-008', ref: 'Art. 18' }, // primary
    { externalId: 'AIACT-ART19', scfId: 'SCF-AIG-007', ref: 'Art. 19' }, // primary
    { externalId: 'AIACT-ART20', scfId: 'SCF-INC-003', ref: 'Art. 20' },
    { primary: true, externalId: 'AIACT-ART20', scfId: 'SCF-AIG-016', ref: 'Art. 20' }, // primary
    { externalId: 'AIACT-ART21', scfId: 'SCF-GOV-005', ref: 'Art. 21' }, // primary
    { externalId: 'AIACT-ART22', scfId: 'SCF-AIG-012', ref: 'Art. 22' }, // primary
    { externalId: 'AIACT-ART23', scfId: 'SCF-VDR-001', ref: 'Art. 23' },
    { primary: true, externalId: 'AIACT-ART23', scfId: 'SCF-AIG-012', ref: 'Art. 23' }, // primary
    { externalId: 'AIACT-ART24', scfId: 'SCF-VDR-001', ref: 'Art. 24' },
    { primary: true, externalId: 'AIACT-ART24', scfId: 'SCF-AIG-012', ref: 'Art. 24' }, // primary
    { externalId: 'AIACT-ART25', scfId: 'SCF-AIG-012', ref: 'Art. 25' }, // primary
    { externalId: 'AIACT-ART26', scfId: 'SCF-AIG-011', ref: 'Art. 26' }, // primary
    { externalId: 'AIACT-ART27', scfId: 'SCF-AIG-014', ref: 'Art. 27' }, // primary

    // ── Ch. III §4/5 — Conformity assessment and registration ───────────
    { externalId: 'AIACT-ART40', scfId: 'SCF-GOV-009', ref: 'Art. 40' },
    { primary: true, externalId: 'AIACT-ART40', scfId: 'SCF-AIG-015', ref: 'Art. 40' }, // primary
    { externalId: 'AIACT-ART41', scfId: 'SCF-AIG-015', ref: 'Art. 41' }, // primary
    { externalId: 'AIACT-ART43', scfId: 'SCF-AIG-015', ref: 'Art. 43' }, // primary
    { externalId: 'AIACT-ART47', scfId: 'SCF-AIG-015', ref: 'Art. 47' }, // primary
    { externalId: 'AIACT-ART48', scfId: 'SCF-AIG-015', ref: 'Art. 48' }, // primary
    { externalId: 'AIACT-ART49', scfId: 'SCF-AIG-015', ref: 'Art. 49' }, // primary

    // ── Ch. IV — Transparency ───────────────────────────────────────────
    { externalId: 'AIACT-ART50', scfId: 'SCF-AIG-010', ref: 'Art. 50' },
    { primary: true, externalId: 'AIACT-ART50', scfId: 'SCF-AIG-008', ref: 'Art. 50' }, // primary — user information/transparency (technical documentation & user info)

    // ── Ch. V — General-purpose AI (GPAI) ───────────────────────────────
    { externalId: 'AIACT-ART51', scfId: 'SCF-AIG-017', ref: 'Art. 51' }, // primary — systemic-risk classification
    { externalId: 'AIACT-ART52', scfId: 'SCF-AIG-017', ref: 'Art. 52' }, // primary — systemic-risk notification
    { externalId: 'AIACT-ART53', scfId: 'SCF-AIG-017', ref: 'Art. 53' }, // primary
    { externalId: 'AIACT-ART54', scfId: 'SCF-AIG-017', ref: 'Art. 54' }, // primary
    { externalId: 'AIACT-ART55', scfId: 'SCF-AIG-017', ref: 'Art. 55' }, // primary
    { externalId: 'AIACT-ART56', scfId: 'SCF-GOV-006', ref: 'Art. 56' },
    { primary: true, externalId: 'AIACT-ART56', scfId: 'SCF-AIG-017', ref: 'Art. 56' }, // primary

    // ── Ch. VII — EU database ───────────────────────────────────────────
    { externalId: 'AIACT-ART71', scfId: 'SCF-AIG-015', ref: 'Art. 71' }, // primary

    // ── Ch. IX — Post-market monitoring, incident reporting, surveillance
    { externalId: 'AIACT-ART72', scfId: 'SCF-AIG-016', ref: 'Art. 72' }, // primary
    { externalId: 'AIACT-ART73', scfId: 'SCF-INC-006', ref: 'Art. 73' },
    { primary: true, externalId: 'AIACT-ART73', scfId: 'SCF-AIG-016', ref: 'Art. 73' }, // primary
    { externalId: 'AIACT-ART79', scfId: 'SCF-AIG-016', ref: 'Art. 79' }, // primary

    // ── Ch. X — Codes of conduct ────────────────────────────────────────
    { externalId: 'AIACT-ART95', scfId: 'SCF-GOV-006', ref: 'Art. 95' },
    { primary: true, externalId: 'AIACT-ART95', scfId: 'SCF-AIG-001', ref: 'Art. 95' }, // primary

    // ── Ch. XII — Penalties (legal register) ────────────────────────────
    { externalId: 'AIACT-ART99', scfId: 'SCF-GOV-009', ref: 'Art. 99' }, // primary
  ],
};

module.exports = {
  FRAMEWORK_EU_AI_ACT,
};
